import React, { ReactNode } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { MapPin, Warehouse as WarehouseIcon } from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

export type WarehouseItem = {
  id: string;
  name: string;
  description: string;
  address: string;
  lat: number;
  lng: number;
  photoURL?: string;
  createdAt: number;
  updatedAt?: number;
  isActive?: boolean;
  isDefaultDispatch?: boolean;
  supportsPickup?: boolean;
  supportsDelivery?: boolean;
};

type WarehouseCardProps = {
  warehouse: WarehouseItem;
  onEdit: (warehouse: WarehouseItem) => void;
  onDelete: (warehouse: WarehouseItem) => void;
  deleting?: boolean;
  children?: ReactNode;
};

const cardsBorderRadii ="8px";
const WarehouseCard: React.FC<WarehouseCardProps> = ({
  warehouse,
  onEdit,
  onDelete,
  deleting = false,
  children,
}) => {
  const { theme } = useCustomTheme();

  const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(
    `${warehouse.lat},${warehouse.lng}`,
  )}`;

  return (
    <Box
      border="1px solid"
      borderColor={theme.border}
      borderRadius={cardsBorderRadii}
      bg={theme.surfaceAlt}
      overflow="hidden"
      transition="border-color 0.18s ease, box-shadow 0.18s ease"
      _hover={{
        borderColor: theme.borderStrong || theme.border,
        boxShadow: theme.shadowSm,
      }}
    >
      <Flex
        p={{ base: 3, md: 4 }}
        gap={{ base: 3, md: 4 }}
        direction={{ base: "column", sm: "row" }}
        align={{ base: "stretch", sm: "flex-start" }}
      >
        <Box
          w={{ base: "100%", sm: "94px" }}
          h={{ base: "150px", sm: "94px" }}
          flexShrink={0}
          borderRadius={cardsBorderRadii}
          overflow="hidden"
          border="1px dashed"
          borderColor={theme.borderStrong || theme.border}
          bg={theme.surface}
        >
          {warehouse.photoURL ? (
            <Image
              src={warehouse.photoURL}
              alt={warehouse.name}
              w="100%"
              h="100%"
              objectFit="cover"
            />
          ) : (
            <Flex w="100%" h="100%" align="center" justify="center">
              <WarehouseIcon size={34} color={theme.textMuted} />
            </Flex>
          )}
        </Box>

        <Flex
          minW={0}
          flex={1}
          direction="column"
          justify="space-between"
          gap={3}
        >
          <Flex gap={3} justify="space-between" align="flex-start" minW={0}>
            <Box minW={0} flex={1}>
              <Text
                color={theme.text}
                fontWeight="800"
                fontSize={{ base: "md", md: "lg" }}
                noOfLines={1}
              >
                {warehouse.name || "Warehouse"}
              </Text>

              {warehouse.description ? (
                <Text
                  mt={0.5}
                  fontSize="sm"
                  color={theme.textMuted}
                  noOfLines={2}
                >
                  {warehouse.description}
                </Text>
              ) : null}

              <HStack mt={2.5} spacing={2} align="flex-start">
                <Box pt="1px" flexShrink={0}>
                  <MapPin size={17} color={theme.textMuted} />
                </Box>
                <Text
                  fontSize="sm"
                  color={theme.text}
                  noOfLines={{ base: 2, md: 1 }}
                >
                  {warehouse.address || "No address"}
                </Text>
              </HStack>
            </Box>

            <Badge
              flexShrink={0}
              colorScheme={warehouse.isActive === false ? "gray" : "green"}
              variant="subtle"
              borderRadius={cardsBorderRadii}
              px={2}
              py={0.5}
              fontSize="xs"
            >
              {warehouse.isActive === false ? "INACTIVE" : "ACTIVE"}
            </Badge>
          </Flex>

          <Flex
            justify="space-between"
            align={{ base: "stretch", sm: "center" }}
            direction={{ base: "column", sm: "row" }}
            gap={2}
          >
            <Button
              as="a"
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
              size="sm"
              variant="outline"
              alignSelf={{ base: "stretch", sm: "flex-start" }}
              borderRadius={cardsBorderRadii}
              borderColor={theme.borderStrong || theme.border}
              color={theme.text}
              bg={theme.surface}
              _hover={{ bg: theme.hover }}
            >
              View on map
            </Button>

            <HStack
              justify={{ base: "flex-end", sm: "flex-start" }}
              spacing={2}
            >
              <IconButton
                aria-label={`Edit ${warehouse.name}`}
                icon={<EditIcon />}
                size="sm"
                variant="outline"
                borderRadius={cardsBorderRadii}
                borderColor={theme.borderStrong || theme.border}
                color={theme.text}
                bg={theme.surface}
                _hover={{ bg: theme.hover }}
                onClick={() => onEdit(warehouse)}
              />

              <IconButton
                aria-label={`Delete ${warehouse.name}`}
                icon={<DeleteIcon />}
                size="sm"
                variant="outline"
                colorScheme="red"
                borderRadius={cardsBorderRadii}
                isLoading={deleting}
                onClick={() => onDelete(warehouse)}
              />
            </HStack>
          </Flex>
        </Flex>
      </Flex>

      {children ? (
        <Box
          borderTop="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
        >
          {children}
        </Box>
      ) : null}
    </Box>
  );
};

export default WarehouseCard;
